import { useMemo, useState } from 'react';
import type { BroadcastBot, BroadcastsBot, FiltrosBot } from '@/services/api/analytics-bot';
import { comparaNulo, n, pct, TRACO, usd, usd4 } from '../format';
import BroadcastAberto from './BroadcastAberto';
import Avisos from './Avisos';
import EstadoBloco from './EstadoBloco';

const GB = '24px minmax(240px,1.6fr) 84px 88px 70px 76px 80px 62px 104px 92px';

type ChaveOrd = 'enviados' | 'entregues' | 'taxa_entrega_pct' | 'falhas' | 'cliques' | 'ctr_pct' | 'receita' | 'rpc';

const SELETOR: [ChaveOrd, string][] = [
    ['receita', 'Receita'], ['rpc', 'RPC'], ['ctr_pct', 'CTR'], ['cliques', 'Cliques'], ['enviados', 'Enviados'],
];

const COLS: [ChaveOrd, string][] = [
    ['enviados', 'Enviados'], ['entregues', 'Entregues'], ['taxa_entrega_pct', 'Entrega'], ['falhas', 'Falhas'],
    ['cliques', 'Cliques'], ['ctr_pct', 'CTR'], ['receita', 'Receita'], ['rpc', 'RPC'],
];

const PILL: Record<BroadcastBot['status'], string> = {
    enviado: 'ok', enviando: 'run', pendente: 'wait', falhou: 'off', recorrente: 'run',
};

interface ListaBroadcastsProps {
    dados: BroadcastsBot | undefined;
    filtros: FiltrosBot;
    rotulo: string;
    variosDias: boolean;
    aberto: number | null;
    onAbrir: (id: number | null) => void;
    carregando: boolean;
    atualizando: boolean;
    erro: boolean;
    onTentar: () => void;
}

/** Top 3 por receita em destaque + lista expansível ordenável. */
export default function ListaBroadcasts({
    dados, filtros, rotulo, variosDias, aberto, onAbrir, carregando, atualizando, erro, onTentar,
}: ListaBroadcastsProps) {
    const [ord, setOrd] = useState<{ c: ChaveOrd; desc: boolean }>({ c: 'receita', desc: true });
    const ordenar = (c: ChaveOrd) => setOrd(o => ({ c, desc: o.c === c ? !o.desc : true }));

    const lista = useMemo(() => {
        const a = [...(dados?.broadcasts ?? [])];
        a.sort((x, y) => comparaNulo(x[ord.c], y[ord.c], ord.desc) || comparaNulo(x.cliques, y.cliques, true));
        return a;
    }, [dados, ord]);

    const top3 = useMemo(
        () => [...(dados?.broadcasts ?? [])]
            .filter(b => typeof b.receita === 'number' && b.receita > 0)
            .sort((a, b) => comparaNulo(a.receita, b.receita, true))
            .slice(0, 3),
        [dados],
    );

    return (
        <div className="card">
            <div className="card-h">
                <div>
                    <div className="card-t">Broadcasts · {rotulo}</div>
                    <div className="card-s">
                        {variosDias
                            ? 'Cada linha é um disparo recorrente somado no período. Clique para ver a criativa e as quebras.'
                            : 'Cada disparo individualmente. Clique para ver a criativa e as quebras.'}
                    </div>
                </div>
                <div className="seg">
                    {SELETOR.map(([k, l]) => (
                        <button key={k} type="button" data-on={ord.c === k ? 1 : 0} onClick={() => ordenar(k)}>{l}</button>
                    ))}
                </div>
            </div>

            {!dados ? (
                <EstadoBloco carregando={carregando} erro={erro} onTentar={onTentar} />
            ) : (
                <div data-loading={atualizando ? 1 : 0}>
                    {top3.length > 0 && (
                        <div className="top3">
                            {top3.map((b, i) => (
                                <div className="t3" key={b.id} data-open={aberto === b.id ? 1 : 0}
                                    onClick={() => onAbrir(aberto === b.id ? null : b.id)}>
                                    <div className="t3-th"><span>Top</span><b>{i + 1}</b></div>
                                    <div className="t3-b">
                                        <div className="t3-n">{b.nome} · {b.hora}</div>
                                        <div className="t3-c">{b.copy?.texto || b.copy?.titulo || TRACO}</div>
                                        <div className="t3-m">
                                            <div>Receita<b className="rev">{usd(b.receita)}</b></div>
                                            <div>RPC<b>{usd4(b.rpc)}</b></div>
                                            <div>Cliques<b>{n(b.cliques)}</b></div>
                                            <div>CTR<b style={{ color: 'var(--violet)' }}>{pct(b.ctr_pct)}</b></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {lista.length === 0 ? (
                        <div className="empty">Nenhum broadcast com esse recorte de filtros.</div>
                    ) : (
                        <div className="lst">
                            <div>
                                <div className="lst-head" style={{ gridTemplateColumns: GB }}>
                                    <span /><span>Disparo</span>
                                    {COLS.map(([k, l]) => (
                                        <span key={k} data-sort="1" data-s={ord.c === k ? 1 : 0} onClick={() => ordenar(k)}>
                                            {l}{ord.c === k ? (ord.desc ? ' ↓' : ' ↑') : ''}
                                        </span>
                                    ))}
                                </div>
                                {lista.map((b, i) => {
                                    const open = aberto === b.id;
                                    const baixa = typeof b.taxa_entrega_pct === 'number' && b.taxa_entrega_pct < 80;
                                    return (
                                        <div className="item" key={b.id} data-open={open ? 1 : 0}>
                                            <div className="lst-row" style={{ gridTemplateColumns: GB }} onClick={() => onAbrir(open ? null : b.id)}>
                                                <div className="cr">{open ? '▾' : '▸'}</div>
                                                <div className="nm" title={b.nome}>
                                                    <span className="rank" data-top={ord.c === 'receita' && ord.desc && i < 3 ? 1 : 0}>#{i + 1}</span>{' '}
                                                    {b.nome} <span className="code">{b.hora}</span>
                                                    <span className="pill" data-t={PILL[b.status] ?? 'wait'}>
                                                        {b.status === 'recorrente' ? `${b.ocorrencias}× enviado` : b.status}
                                                    </span>
                                                    <small>grupo #{b.id}{b.ocorrencias > 1 ? ` · ${b.ocorrencias} grupos somados` : ''}</small>
                                                </div>
                                                <div className="num">{n(b.enviados)}</div>
                                                <div className="num">{n(b.entregues)}</div>
                                                <div className="num" style={{ color: baixa ? 'var(--red)' : 'inherit' }}>{pct(b.taxa_entrega_pct, 1)}</div>
                                                <div className="num">{n(b.falhas)}</div>
                                                <div className="num" style={{ fontWeight: 620 }}>{n(b.cliques)}</div>
                                                <div className="num" style={{ color: 'var(--violet)' }}>{pct(b.ctr_pct)}</div>
                                                <div className="num rev">{usd(b.receita)}</div>
                                                <div className="num muted">{usd4(b.rpc)}</div>
                                            </div>
                                            {open && (
                                                <BroadcastAberto b={b} filtros={filtros} rotulo={rotulo} onFechar={() => onAbrir(null)} />
                                            )}
                                        </div>
                                    );
                                })}
                                <div className="item" data-open={0} data-fixed={1}>
                                    <div className="lst-row" style={{ gridTemplateColumns: GB }}>
                                        <div className="cr" />
                                        <div className="nm">
                                            Não atribuído
                                            <small>receita de broadcast que não se liga a nenhum grupo listado</small>
                                        </div>
                                        {COLS.slice(0, 6).map(([k]) => <div key={k} className="num muted">{TRACO}</div>)}
                                        <div className="num rev">{usd(dados.receita_nao_atribuida)}</div>
                                        <div className="num muted">{TRACO}</div><div className="num muted">{TRACO}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            <Avisos avisos={dados?.avisos} />
        </div>
    );
}
